import React, { useState, useEffect, useMemo, useContext } from "react";
// Chart.js global görünümü (TMS Control Tower dili); chartDarkMode'dan önce yüklenmeli
import "assets/theme/chartDefaults";
import { useAbility } from "@casl/react";
import {
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { setupAxiosInterceptors } from "services/interceptor";
import PageLoader from "./PageLoader";

// MUI & theme
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { SnackbarProvider } from "context";
import { AppDialogHost } from "components/AppDialog";

// Layout & components
import MDBox from "components/MDBox";
import Icon from "@mui/material/Icon";
import MDAvatar from "components/MDAvatar";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import ProtectedRoute from "examples/ProtectedRoute";

// Routes & context
import routes from "routes";
import crudRoutes from "crud.routes";
import { AbilityContext, Can } from "./Can";
import {
    useMaterialUIController,
    setMiniSidenav,
    setOpenConfigurator,
    AuthContext,
} from "context";
import { getPermissions } from "config/Permissions";

// Pages
import Login from "auth/login";
import Register from "auth/register";
import ForgotPassword from "auth/forgot-password";
import ResetPassword from "auth/reset-password";
import ViewShipments from "layouts/ecommerce/products/new-product/crm/invoice/index";
import CreateOpportunity from "layouts/ecommerce/products/new-product/crm/opportunity/CreateOpportunity";

// Themes & assets
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import darkColorAdapter from "assets/theme-dark/darkColorAdapter";
import { setChartDarkMode } from "assets/theme-dark/chartDarkMode";
import "assets/theme-dark/dark-mode.css";
import brandWhite from "assets/images/Filenders.png";
import brandDark from "assets/images/Filenders.png";
import { Helmet } from "react-helmet";

import MblFile from "./layouts/ecommerce/products/new-product/Shipment/MblFile";
import ConnectMBL from "./layouts/ecommerce/products/new-product/Shipment/ConnectMBL";
import ConnectOMBL from "./layouts/ecommerce/products/new-product/OceanTMS/ConnectOMBL";
import ConnectAMBL from "./layouts/ecommerce/products/new-product/AirTMS/ConnectAMBL";
import AnnualLeaveForm from "./layouts/ecommerce/products/new-product/AnnualLeaveForm/AnnualLeaveForm";
import {FinanceJobDetail} from "./layouts/ecommerce/products/new-product/crm/invoice/finance";
import CreateManualInvoice from "layouts/ecommerce/products/new-product/crm/invoice/finance/Manual-Invoice/index";
import CreateReturnInvoice from "./layouts/ecommerce/products/new-product/crm/invoice/finance/ReturnInvoice";
import ReturnInvoiceDetail from "./layouts/ecommerce/products/new-product/crm/invoice/finance/ReturnInvoiceDetail";
import ReconciliationDetail from "./layouts/ecommerce/products/new-product/crm/invoice/finance/Reconciliation/detail";
import ReconciliationResponse from "./layouts/ecommerce/products/new-product/crm/invoice/finance/Reconciliation/public";
const getSidenavColorHex = (color) => {
    const colorMap = {
        primary: "#e10bd0",
        secondary: "#9c27b0",
        info: "#00acc1",
        success: "#4caf50",
        warning: "#fb8c00",
        error: "#f44336",
        dark: "#212121",
        light: "#f5f5f5",
        white: "#ffffff",
    };

    return colorMap[color] || colorMap.info;
};

// Dark mode styles are generated in their own Emotion caches so the color adapter
// never touches light mode output.
const darkCache = createCache({ key: "dark", stylisPlugins: [darkColorAdapter, prefixer] });
const darkRtlCache = createCache({
    key: "dark-rtl",
    stylisPlugins: [darkColorAdapter, prefixer, rtlPlugin],
});

const generateTheme = (baseTheme, sidenavColor) => ({
    ...baseTheme,
    palette: {
        ...baseTheme.palette,
        primary: {
            main: getSidenavColorHex(sidenavColor),
        },
    },
});

export default function App() {
    const ability = useAbility(AbilityContext);

    const [controller, dispatch] = useMaterialUIController();
    const {
        miniSidenav,
        direction,
        layout,
        openConfigurator,
        sidenavColor,
        transparentSidenav,
        whiteSidenav,
        darkMode,
    } = controller;

    const { pathname } = useLocation();
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);

    const [rtlCache, setRtlCache] = useState(null);
    const [onMouseEnter, setOnMouseEnter] = useState(false);
    const [userDetails, setUserDetails] = useState({ name: "", image: "" });
    const [isDemo, setIsDemo] = useState(false);
    const [routesReady, setRoutesReady] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    useEffect(() => {
        setIsDemo(process.env.REACT_APP_IS_DEMO === "true");
    }, []);

    useEffect(() => {
        setupAxiosInterceptors(() => {
            authContext.logout();
            navigate("/auth/login");
        });
    }, [authContext, navigate]);

    useEffect(() => {
        const cacheRtl = createCache({
            key: "rtl",
            stylisPlugins: [rtlPlugin],
        });

        setRtlCache(cacheRtl);
    }, []);

    useEffect(() => {
        setPageLoading(true);

        const timer = setTimeout(() => {
            setPageLoading(false);
        }, 400);

        return () => clearTimeout(timer);
    }, [pathname]);

    useEffect(() => {
        document.body.setAttribute("dir", direction);
    }, [direction]);

    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute("data-theme", darkMode ? "dark" : "light");
        root.style.colorScheme = darkMode ? "dark" : "light";

        setChartDarkMode(darkMode);
    }, [darkMode]);

    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    useEffect(() => {
        if (authContext.isLoading) return;

        if (authContext.isAuthenticated && ability.rules.length > 0) {
            setRoutesReady(true);
        } else if (!authContext.isAuthenticated && !localStorage.getItem("token")) {
            setRoutesReady(true);
        }
    }, [authContext.isAuthenticated, authContext.isLoading, ability.rules]);

    useEffect(() => {
        if (
            authContext.isAuthenticated &&
            !authContext.isLoading &&
            authContext.user?.id
        ) {
            (async () => {
                try {
                    const id = authContext.user.id;

                    setUserDetails({
                        name: authContext.user.name || "User",
                        image: authContext.user.profile_image || "",
                    });

                    const rules = await getPermissions(id);
                    ability.update(rules);
                } catch (err) {
                    console.warn("Yetkisiz istek ya da token hatası:", err);
                }
            })();
        }
    }, [
        authContext.isAuthenticated,
        authContext.isLoading,
        authContext.user?.id,
        ability,
    ]);

    // Keep the sidenav avatar in sync when the profile photo changes.
    useEffect(() => {
        if (!authContext.user?.id) return;
        setUserDetails((prev) => ({ ...prev, image: authContext.user.profile_image || "" }));
    }, [authContext.user?.id, authContext.user?.profile_image]);

    const handleOnMouseEnter = () => {
        if (miniSidenav && !onMouseEnter) {
            setMiniSidenav(dispatch, false);
            setOnMouseEnter(true);
        }
    };

    const handleOnMouseLeave = () => {
        if (onMouseEnter) {
            setMiniSidenav(dispatch, true);
            setOnMouseEnter(false);
        }
    };

    const handleConfiguratorOpen = () => {
        setOpenConfigurator(dispatch, !openConfigurator);
    };

    function filterRoutes(allRoutes) {
        return allRoutes
            .map((route) => {
                if (route.type === "divider" || route.type === "title") return route;

                if (route.collapse) {
                    const filteredCollapse = filterRoutes(route.collapse);
                    return filteredCollapse.length
                        ? { ...route, collapse: filteredCollapse }
                        : null;
                }

                if (route.route) {
                    const subject = route.subject || route.route;
                    const action = route.action || "view";

                    if (ability.can(action, subject)) return route;
                }

                return null;
            })
            .filter(Boolean);
    }

    const filteredRoutes = useMemo(
        () => filterRoutes(routes),
        [routes, ability.rules]
    );

    const filteredCrudRoutes = useMemo(
        () => filterRoutes(crudRoutes),
        [crudRoutes, ability.rules]
    );

    const getRoutes = (allRoutes) =>
        allRoutes.map((route) => {
            if (route.key === "user-name") {
                const name = userDetails?.name || authContext.user?.name || "User";

                const avatarRaw =
                    userDetails?.image ?? authContext.user?.profile_image;

                const avatar =
                    typeof avatarRaw === "string" && avatarRaw.trim() !== ""
                        ? avatarRaw
                        : "https://ui-avatars.com/api/?name=User";

                route.name = typeof name === "string" ? name : "User";
                route.icon = <MDAvatar src={avatar} size="sm" alt={route.name} />;
            }

            if (route.collapse) return getRoutes(route.collapse);

            if (route.route) {
                const subject = route.subject || route.route;
                const action = route.action || "view";

                return (
                    <Route
                        key={route.key}
                        path={route.route}
                        element={
                            <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                                <Can I={action} a={subject}>
                                    {route.component}
                                </Can>
                            </ProtectedRoute>
                        }
                    />
                );
            }

            return null;
        });

    const getCrudRoutes = (allRoutes) =>
        allRoutes.map((route) => {
            if (route.collapse) return getCrudRoutes(route.collapse);

            if (route.route) {
                return (
                    <Route
                        key={route.key}
                        path={route.route}
                        element={
                            <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                                {route.component}
                            </ProtectedRoute>
                        }
                    />
                );
            }

            return null;
        });

    const configsButton = (
        <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="3.25rem"
            height="3.25rem"
            bgColor="white"
            shadow="sm"
            borderRadius="50%"
            position="fixed"
            right="2rem"
            bottom="2rem"
            zIndex={99}
            color="dark"
            sx={{ cursor: "pointer" }}
            onClick={handleConfiguratorOpen}
        >
            <Icon fontSize="small">settings</Icon>
        </MDBox>
    );

    const activeTheme = generateTheme(
        direction === "rtl"
            ? darkMode
                ? themeDarkRTL
                : themeRTL
            : darkMode
                ? themeDark
                : theme,
        sidenavColor
    );

    const renderRoutes = (
        <Routes>
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/register" element={<Register />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            <Route path="/reconciliation-response/:token" element={<ReconciliationResponse />}/>
            <Route path="/create-invoice" element={<ViewShipments />} />
            <Route path="/create-opportunity" element={<CreateOpportunity />} />
            <Route path="/create-opportunity/:id" element={<CreateOpportunity />} />

            <Route path="/shipping-order/create-mbl" element={<MblFile />} />
            <Route path="/shipping-order/connect-mbl" element={<ConnectMBL />} />
            <Route path="/ocean-shipping-order/connect-ombl" element={<ConnectOMBL />} />
            <Route path="/air-shipping-order/connect-ambl" element={<ConnectAMBL />} />
            <Route
                path="/annual-leave/create/:id"
                element={<AnnualLeaveForm />}
            />
            <Route
                path="/invoice/finance/jobs/:id"
                element={<FinanceJobDetail />}
            />
            <Route
                path="/invoice/finance/manual-invoices/:invoiceId/edit"
                element={
                    <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                        <CreateManualInvoice />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/invoice/finance/return-invoices/new"
                element={
                    <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                        <CreateReturnInvoice />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/invoice/finance/return-invoices/:id"
                element={
                    <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                        <ReturnInvoiceDetail />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/invoice/finance/reconciliations/:id"
                element={
                    <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                        <ReconciliationDetail />
                    </ProtectedRoute>
                }
            />

            {getRoutes(filteredRoutes)}
            {getCrudRoutes(filteredCrudRoutes)}

            <Route
                path="/"
                element={
                    authContext.isAuthenticated ? (
                        <Navigate to="/dashboards/analytics" />
                    ) : (
                        <Navigate to="/auth/login" />
                    )
                }
            />

            <Route
                path="*"
                element={
                    authContext.isAuthenticated ? (
                        <Navigate to="/dashboards/analytics" />
                    ) : (
                        <Navigate to="/auth/login" />
                    )
                }
            />
        </Routes>
    );

    const renderLayout = (
        <>
            <CssBaseline />

            {layout === "dashboard" && (
                <>
                    <Sidenav
                        color={sidenavColor}
                        brand={
                            (transparentSidenav && !darkMode) || whiteSidenav
                                ? brandDark
                                : brandWhite
                        }
                        brandName="FILENTECH"
                        routes={filteredRoutes}
                        onMouseEnter={handleOnMouseEnter}
                        onMouseLeave={handleOnMouseLeave}
                    />

                    <Configurator />
                    {configsButton}
                </>
            )}

            {layout === "vr" && <Configurator />}

            {routesReady ? renderRoutes : <PageLoader />}

            {routesReady && pageLoading && <PageLoader />}

            {/* window.confirm / alert yerine kullanılan ortak onay ve bilgi kartları */}
            <AppDialogHost />
        </>
    );

    return (
        <>
            {isDemo && (
                <Helmet>
                    {/* Meta tag örnekleri */}
                </Helmet>
            )}

            {direction === "rtl" || darkMode ? (
                <SnackbarProvider>
                    <CacheProvider
                        value={
                            direction === "rtl" ? (darkMode ? darkRtlCache : rtlCache) : darkCache
                        }
                    >
                        <ThemeProvider theme={activeTheme}>{renderLayout}</ThemeProvider>
                    </CacheProvider>
                </SnackbarProvider>
            ) : (
                <SnackbarProvider>
                    <ThemeProvider theme={activeTheme}>{renderLayout}</ThemeProvider>
                </SnackbarProvider>
            )}
        </>
    );
}